import { useState } from 'react';
import { ArrowRight, X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import type { User } from '../../api/types';
import { needsSignupResume } from '../../utils/needsSignupResume';
import {
  getMissingApprovalCredentials,
  formatMissingSectionsList,
  hasAllRequiredSections,
} from '../../utils/doctorProfileHelpers';

const doshaBannerDismissKey = (userId: string | number) =>
  `meraayurved.dismissDoshaBanner.${userId}`;

function isDoshaBannerDismissedInStorage(userId: string | number): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(doshaBannerDismissKey(userId)) === '1';
  } catch {
    return false;
  }
}

function nonEmptyStr(v: unknown): v is string {
  return typeof v === 'string' && v.trim().length > 0;
}

/**
 * Dosha saved during signup or profile — /users/me may omit dosha_primary, use camelCase,
 * or only return assessment_answers from the questionnaire.
 */
function isDoshaFilled(user: User): boolean {
  if (nonEmptyStr(user.dosha_primary)) return true;
  if (nonEmptyStr(user.dosha_secondary)) return true;
  const r = user as Record<string, unknown>;
  if (nonEmptyStr(r.doshaPrimary)) return true;
  if (nonEmptyStr(r.doshaSecondary)) return true;
  const profile = r.profile;
  if (profile && typeof profile === 'object') {
    const p = profile as Record<string, unknown>;
    if (nonEmptyStr(p.dosha_primary) || nonEmptyStr(p.dosha_secondary))
      return true;
  }
  const ans = r.assessment_answers;
  if (
    ans &&
    typeof ans === 'object' &&
    !Array.isArray(ans) &&
    Object.keys(ans as object).length > 0
  ) {
    return true;
  }
  return false;
}

const ProfileCompletionBanner = () => {
  const { user, isAuthenticated, isLoading, isDoctor } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  /** Re-render after dismiss so localStorage read in render picks up the new value. */
  const [, bumpDoshaDismiss] = useState(0);
  const doshaBannerDismissed =
    !!user?.id && isDoshaBannerDismissedInStorage(user.id);

  if (!isAuthenticated || isLoading || !user) {
    return null;
  }

  /** Same as OnboardingGate — no top strip until signup/onboarding is finished */
  if (needsSignupResume(user)) {
    return null;
  }

  if (user.role === 'admin') {
    return null;
  }

  /** Provider shell only — doctor-account banners must not show on member UI when active profile is patient. */
  const isProviderSession = isDoctor();

  // Avoid duplicate onboarding prompts inside auth/onboarding flows.
  if (location.pathname === '/login' || location.pathname === '/signup') {
    return null;
  }

  // Don't show doctor-approval banner for approved doctors who have all sections completed
  if (
    isProviderSession &&
    user.role === 'doctor' &&
    user.doctorProfile?.status === 'approved' &&
    hasAllRequiredSections(user.doctorProfile, user)
  ) {
    return null;
  }

  const basicProfileComplete = (user.profile_completion_level || 0) >= 3;

  // Determine which banner to show
  let bannerType:
    | 'dosha'
    | 'doctor_incomplete'
    | 'doctor_review_requested'
    | 'doctor_rejected'
    | null = null;

  const isDoshaComplete = isDoshaFilled(user);

  // PRIORITY 1: Doctor provider profile (only while active session is provider — not on member UI)
  if (isProviderSession && user?.role === 'doctor') {
    const dp = user.doctorProfile;
    const missingApproval = getMissingApprovalCredentials(dp, user);
    const hasAllSections = missingApproval.length === 0;
    // Rejected takes precedence
    if (dp?.status === 'rejected') {
      bannerType = 'doctor_rejected';
    } else if (dp?.status === 'review_requested') {
      bannerType = 'doctor_review_requested';
    } else if (!dp || dp.status !== 'approved' || !hasAllSections) {
      // Not submitted yet, or approved profile later lost required credentials.
      bannerType = 'doctor_incomplete';
    }
  }

  const isDoctorAccountInPatientMode =
    user?.role === 'doctor' && !isProviderSession;

  // Dosha reminder only after basic profile is complete (level ≥ 3). Medical is optional in signup — no global nag.
  if (!bannerType && basicProfileComplete && !isDoshaComplete) {
    if (isDoctorAccountInPatientMode) {
      bannerType = 'dosha';
    } else {
      const isDoctorWithCompleteProfile =
        user?.role === 'doctor' &&
        hasAllRequiredSections(user?.doctorProfile, user);
      if (!isDoctorWithCompleteProfile) {
        bannerType = 'dosha';
      }
    }
  }

  if (!bannerType) return null;
  if (bannerType === 'dosha' && doshaBannerDismissed) return null;

  const dismissDoshaBanner = () => {
    if (!user?.id) return;
    try {
      localStorage.setItem(doshaBannerDismissKey(user.id), '1');
    } catch {
      /* ignore quota / private mode */
    }
    bumpDoshaDismiss((n) => n + 1);
  };

  /** Patient shell: /profile Dosha accordion (`id="dosha"`). Provider session uses doctor profile. */
  const goToDoshaInProfile = () => {
    if (isProviderSession && user.role === 'doctor') {
      navigate('/doctor/profile#dosha-section');
      return;
    }
    navigate('/profile#dosha');
  };

  return (
    <div className="bg-[#7E8D4C] text-white py-3 px-4 relative transition-all duration-300 shadow-md">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-3 sm:gap-4">
        <div className="flex-1 flex flex-wrap items-center justify-center sm:justify-start gap-x-3 gap-y-1 text-sm sm:text-base font-medium min-w-0">
          {bannerType === 'dosha' && (
            <>
              <span className="text-center sm:text-left">
                Complete your profile — add your dosha for personalized
                remedies.
              </span>
              <button
                type="button"
                onClick={goToDoshaInProfile}
                className="underline hover:text-green-100 flex items-center gap-1 shrink-0 bg-transparent border-none cursor-pointer text-white"
              >
                Complete Profile <ArrowRight className="w-4 h-4" />
              </button>
            </>
          )}
          {bannerType === 'doctor_incomplete' &&
            (() => {
              const dp = user.doctorProfile;
              const missing = getMissingApprovalCredentials(dp, user);
              const missingList = formatMissingSectionsList(missing);
              const firstMissing = missing[0];
              const profilePath = '/doctor/profile';
              const targetPath = firstMissing
                ? `${profilePath}#${firstMissing.sectionId}`
                : profilePath;
              const wasApprovedButDeleted =
                dp?.status === 'approved' && missing.length > 0;
              return (
                <button
                  onClick={() => navigate(targetPath)}
                  className="w-full flex items-center justify-center sm:justify-start gap-3 text-left bg-transparent border-none cursor-pointer py-0"
                >
                  <span>
                    {wasApprovedButDeleted
                      ? `Your profile is Approved, but remedy creation requires completing: ${missingList}.`
                      : missingList
                        ? `Complete your profile and submit it for approval. Still required: ${missingList}.`
                        : 'Submit your profile for approval.'}
                  </span>
                  <span className="underline hover:text-green-100 flex items-center gap-1 shrink-0">
                    {wasApprovedButDeleted
                      ? 'Complete Sections'
                      : missingList
                        ? 'Complete Profile'
                        : 'Submit'}{' '}
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </button>
              );
            })()}
          {bannerType === 'doctor_review_requested' && (
            <>
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />
                Profile Submitted for Review - Awaiting Admin Approval
              </span>
              <button
                onClick={() => navigate('/doctor/profile')}
                className="underline hover:text-green-100 flex items-center gap-1 shrink-0 bg-transparent border-none cursor-pointer"
              >
                View Profile <ArrowRight className="w-4 h-4" />
              </button>
            </>
          )}
          {bannerType === 'doctor_rejected' && (
            <>
              <span>
                Your doctor profile was rejected. Please update your profile for
                re-approval.
              </span>
              <button
                type="button"
                onClick={() => {
                  const missing = getMissingApprovalCredentials(
                    user?.doctorProfile,
                    user,
                  );
                  const hash = missing[0]?.sectionId ?? 'work-experience';
                  navigate(`/doctor/profile#${hash}`);
                }}
                className="underline hover:text-green-100 flex items-center gap-1 shrink-0 bg-transparent border-none cursor-pointer"
              >
                Update Profile <ArrowRight className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
        {bannerType === 'dosha' && (
          <button
            type="button"
            onClick={dismissDoshaBanner}
            className="shrink-0 p-1.5 rounded-lg hover:bg-white/15 text-white/90 hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
            aria-label="Dismiss dosha reminder"
          >
            <X className="w-5 h-5" strokeWidth={2.25} />
          </button>
        )}
      </div>
    </div>
  );
};

export default ProfileCompletionBanner;
